import {
  HiddenBelow,
  InlineImageBlockContentLayout,
  type ActionGroupFragment,
  type BoxFragment,
  type ImageBlockFragment,
  type ImageBlockGroupFragment,
} from '@seek/cmsu-cms-connect';
import { Box, Column, Columns, Stack } from 'braid-design-system';
import { type ComponentProps, useMemo } from 'react';

import {
  getBlockBorderProps,
  getBoxBackgroundProps,
} from '../../shared/helper';
import { Asset, AssetType } from '../Asset/Asset';
import { Heading } from '../Heading/Heading';
import { Paragraph } from '../Paragraph/Paragraph';

import { getAccentBarStyle } from './AccentBar';
import { ActionGroup } from '../ActionGroup/ActionGroup';

type Props = Pick<
  ImageBlockFragment,
  | 'heading'
  | 'image'
  | 'paragraph'
  | 'cButtonLink'
  | 'cTextLink'
  | 'dialogButton'
  | 'reverseContent'
  | 'accentBar'
> &
  Pick<
    ImageBlockGroupFragment,
    | 'imageAlignment'
    | 'blockBackground'
    | 'blockBorder'
    | 'stackBelow'
    | 'inlineBlockContentLayout'
  > & {
    inlineTextJustify?: BoxFragment['justifyContent'];
    actions?: Omit<ActionGroupFragment, 'locale'>;
  };

const getCollapseProp = (stackBelow: ImageBlockGroupFragment['stackBelow']) => {
  switch (stackBelow) {
    case HiddenBelow.Tablet:
      return 'tablet';
    case HiddenBelow.Desktop:
      return 'desktop';
    default:
      return undefined;
  }
};

const getDesktopContentReversal = (
  reverseContent: ImageBlockFragment['reverseContent'],
  stackBelow: ImageBlockGroupFragment['stackBelow'],
) => {
  if (!stackBelow || stackBelow === HiddenBelow.None) {
    return false;
  }
  switch (reverseContent) {
    case 'mobile':
    case 'desktop':
      return true;
    case 'both':
    default:
      return false;
  }
};

const getAlignYProp = (inlineTextJustify: BoxFragment['justifyContent']) => {
  switch (inlineTextJustify) {
    case 'flexStart':
      return 'top';
    case 'flexEnd':
      return 'bottom';
    case 'center':
      return 'center';
    default:
      return 'top';
  }
};

const getColumnGap = (image: ImageBlockFragment['image']) => {
  if (!image) {
    return 'none';
  }

  if (image.width) {
    if (image.width > 100) {
      return 'gutter';
    }
    if (image.width > 70) {
      return 'large';
    }
    if (image.width > 40) {
      return 'small';
    }
    if (image.width > 0) {
      return 'xsmall';
    }
  }
  // Default to "medium" for images that have a width of 0.
  // This often happens with incorrectly coded SVGs.
  return 'medium';
};

export const InlineImageBlock = ({
  imageAlignment,
  image,
  heading,
  paragraph,
  actions,
  stackBelow = HiddenBelow.None,
  inlineTextJustify,
  blockBackground,
  blockBorder,
  reverseContent,
  accentBar,
  inlineBlockContentLayout,
}: Props) => {
  const ImageComponent = useMemo(
    () =>
      image ? (
        <Box display="flex" alignItems={imageAlignment || 'flexStart'}>
          <Asset
            assetType={AssetType.DEFAULT_IMAGE}
            {...image}
            style={{ maxWidth: 'fit-content' }}
          />
        </Box>
      ) : null,
    [image, imageAlignment],
  );

  const ContentComponent = useMemo(() => {
    const hasCta = actions;

    const boxProps = boxLayout(inlineBlockContentLayout);

    return (
      <Box
        width="full"
        display="flex"
        flexDirection="column"
        justifyContent={inlineTextJustify ?? 'center'}
      >
        <Box {...boxProps}>
          <Stack space="small">
            {heading && <Heading {...heading} />}
            {paragraph && (
              <Paragraph
                {...paragraph}
                content={paragraph.Paragraph_text?.raw}
              />
            )}
          </Stack>
          {hasCta ? <ActionGroup {...actions} /> : null}
        </Box>
      </Box>
    );
  }, [
    actions,
    heading,
    paragraph,
    inlineTextJustify,
    inlineBlockContentLayout,
  ]);

  // Given {image} is content #1, and {content} is content #2,
  // we need to set the order of the content based on the reverseContent value
  const getContentOrder = useMemo(
    () => (reverse: ImageBlockFragment['reverseContent']) => {
      const firstCol = (
        <Column width="content" key="image">
          {ImageComponent}
        </Column>
      );
      const secondCol = <Column key="content">{ContentComponent}</Column>;

      switch (reverse) {
        case 'both':
        case 'mobile':
          return [secondCol, firstCol];
        case 'desktop':
        default:
          return [firstCol, secondCol];
      }
    },
    [ImageComponent, ContentComponent],
  );
  const { background, style: backgroundStyle } =
    getBoxBackgroundProps(blockBackground);
  return (
    <Box
      padding={blockBackground ? 'gutter' : 'none'}
      borderRadius={blockBackground ? 'large' : 'none'}
      height="full"
      {...getBlockBorderProps(blockBorder)}
      background={background}
      style={{
        ...backgroundStyle,
        boxShadow: getAccentBarStyle(accentBar, blockBorder),
      }}
    >
      {stackBelow !== HiddenBelow.None ? (
        <Box>
          <Columns
            space={getColumnGap(image)}
            alignY={getAlignYProp(imageAlignment)}
            collapseBelow={getCollapseProp(stackBelow)}
            reverse={getDesktopContentReversal(reverseContent, stackBelow)}
          >
            {getContentOrder(reverseContent)}
          </Columns>
        </Box>
      ) : (
        <Box
          display="flex"
          flexDirection={
            reverseContent === 'desktop' || reverseContent === 'both'
              ? 'rowReverse'
              : 'row'
          }
          gap={getColumnGap(image)}
          alignItems={imageAlignment || 'center'}
        >
          {ImageComponent}
          {ContentComponent}
        </Box>
      )}
    </Box>
  );
};

// Base responsive configuration that's common for boxLayout
const baseBoxLayoutConfig: ComponentProps<typeof Box> = {
  display: 'flex',
  flexDirection: {
    desktop: 'row',
    mobile: 'column',
  },
  gap: 'large',
  alignItems: {
    desktop: 'center',
    mobile: 'flexStart',
  },
};

const boxLayout = (
  inlineBlockContentLayout: InlineImageBlockContentLayout | null | undefined,
): ComponentProps<typeof Box> => {
  switch (inlineBlockContentLayout) {
    case InlineImageBlockContentLayout.Spread:
      return {
        ...baseBoxLayoutConfig,
        justifyContent: {
          desktop: 'spaceBetween',
          mobile: 'flexStart',
        },
      };
    case InlineImageBlockContentLayout.Compact:
      return {
        ...baseBoxLayoutConfig,
        justifyContent: 'flexStart',
      };
    default:
      return {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flexStart',
        gap: 'large',
      };
  }
};
